import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import TicketCard from '../components/TicketCard';
import DebugPanel from '../components/DebugPanel';
import firebaseService from '../services/firebaseService';
import ticketUpdateService from '../services/ticketUpdateService';
import './Live.css';

const TBILISI_TIME_ZONE = 'Asia/Tbilisi';

const parseTbilisiMatchDate = (match) => {
  if (!match?.date || !match?.matchTime) return null;
  const dateOnly = match.date.split('T')[0];
  const time = String(match.matchTime).length === 5 ? `${match.matchTime}:00` : match.matchTime;
  const parsed = new Date(`${dateOnly}T${time}+04:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatTbilisiMatchTime = (match) => {
  const parsed = parseTbilisiMatchDate(match);
  if (!parsed) return match?.matchTime || '';
  return new Intl.DateTimeFormat('ka-GE', {
    timeZone: TBILISI_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).format(parsed);
};

const withTbilisiTime = (matches) => matches.map(match => ({
  ...match,
  matchTime: formatTbilisiMatchTime(match)
}));

const Live = () => {
  const [liveMatches, setLiveMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTbilisiTime, setCurrentTbilisiTime] = useState(new Date());
  const [updatingCards, setUpdatingCards] = useState(new Set());



  useEffect(() => {
    let unsubscribe = null;
    let isMounted = true;

    // Helper function to check if a match is currently live based on Tbilisi time
    const isMatchLive = (match) => {
      if (String(match.status).toLowerCase() === 'live') return true;

      const matchDateTime = parseTbilisiMatchDate(match);
      if (!matchDateTime) return false;
      const matchEndTime = new Date(matchDateTime.getTime() + 120 * 60000);
      const now = new Date();
      return now >= matchDateTime && now <= matchEndTime;
    };

    const setupSubscription = async () => {
      try {
        console.log('📡 Live: Setting up real-time subscription...');

        // Set up real-time listener for automatic updates
        unsubscribe = await firebaseService.subscribeToMatches((updatedMatches) => {
          if (!isMounted) return;

          console.log('📡 Live: Real-time update received -', updatedMatches?.length || 0, 'matches');

          if (!Array.isArray(updatedMatches)) {
            console.error('❌ Live: Subscription callback received non-array:', updatedMatches);
            return;
          }

          // Filter only truly live matches based on status or time
          let newLive = updatedMatches.filter(match => isMatchLive(match));
          console.log('📊 Live: Real-time filtered - Live:', newLive.length);

          setLiveMatches(withTbilisiTime(newLive));
          // Track which cards are being updated (for animation)
          const updatingIds = new Set();
          if (updatingIds.size > 0) {
            setUpdatingCards(updatingIds);
            // Clear the updating state after animation
            setTimeout(() => {
              if (isMounted) setUpdatingCards(new Set());
            }, 1000);
          }
        });

        console.log('✅ Live: Real-time subscription established successfully');
      } catch (error) {
        console.error('❌ Live: Error setting up subscription:', error);
      }
    };

    const fetchData = async () => {
      if (!isMounted) return;

      setLoading(true);

      try {
        console.log('🔄 Live: Starting to fetch matches from Firebase...');

        // Load ticket matches from Firebase
        const matches = await firebaseService.getAllMatches();
        console.log('📥 Live: getAllMatches returned', matches?.length || 0, 'matches');

        if (!isMounted) return;

        if (!Array.isArray(matches)) {
          console.error('❌ Live: getAllMatches did not return an array:', matches);
          setLiveMatches([]);
          return;
        }

        // Filter only truly live matches based on status or time
        let live = matches.filter(match => isMatchLive(match));
        console.log('📊 Live: Filtered - Live:', live.length, 'Total:', matches.length);

        console.log('✅ Live: Setting state - Live:', live.length, 'matches');
        setLiveMatches(withTbilisiTime(live));
      } catch (error) {
        console.error('❌ Live: Error loading matches:', error);
        if (!isMounted) return;
        setLiveMatches([]);
      }

      if (isMounted) {
        setLoading(false);
        console.log('✅ Live: Initial load complete');
      }
    };

    fetchData();
    setupSubscription();

    // Set up ticket update service callback
    const updateCallback = (updatedTickets) => {
      if (!isMounted) return;
      console.log('Tickets updated:', updatedTickets);
    };

    ticketUpdateService.onUpdate(updateCallback);

    const clockInterval = setInterval(() => setCurrentTbilisiTime(new Date()), 1000);

    // Cleanup function
    return () => {
      isMounted = false;
      if (unsubscribe && typeof unsubscribe === 'function') {
        try {
          unsubscribe();
        } catch (error) {
          console.error('Error unsubscribing:', error);
        }
      }
      ticketUpdateService.offUpdate(updateCallback);
      clearInterval(clockInterval);
    };
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString('ka-GE', {
      timeZone: TBILISI_TIME_ZONE,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    });
  };



  if (loading) {
    return (
      <div className="live">
        <div className="container">
          <div className="loading-container">
            <div className="spinner"></div>
            <p className="loading-text">იტვირთება ლაივ მონაცემები...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="live"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <motion.h1
            className="page-title"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            LIVE <span className="live-title-dot" aria-hidden="true" />
          </motion.h1>
          <motion.p
            className="page-subtitle"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            მატჩები რეალურ დროში
          </motion.p>
        </div>

        {/* Live Status */}
        <motion.div
          className="live-status"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <div className="status-indicator">
            <div className="live-dot"></div>
            <span className="status-text">ლაივ რეჟიმი</span>
          </div>
          <div className="last-update">
            თბილისის დრო: {formatTime(currentTbilisiTime)}
          </div>
        </motion.div>

        <motion.div
          className="live-matches"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          {liveMatches.length > 0 ? (
            <div className="matches-grid">
              <div className="tc-header">
                <span className="tc-header-label">კლუბები</span>
                <span className="tc-header-label">პროგნოზი</span>
                <span className="tc-header-label">კოეფ. | თარიღი</span>
              </div>
              {liveMatches.map((match, index) => (
                <TicketCard
                  key={match.id}
                  ticket={match}
                  isLive={true}
                  index={index}
                  isUpdating={updatingCards.has(match.id)}
                />
              ))}
            </div>
          ) : (
            <div className="empty-container">
              <div className="empty-icon">🎫</div>
              <p className="empty-message">ლაივი იქნება შესვენებებზე</p>
            </div>
          )}
        </motion.div>

        {/* Debug Panel - only show in development or when explicitly enabled */}
        {(process.env.NODE_ENV === 'development' || window.location.search.includes('debug=true')) && (
          <DebugPanel />
        )}

      </div>
    </motion.div>
  );
};

export default Live;
